import { useState } from "react";
import { Save, Scale, Send, Radar, Ruler, Usb } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { useApp } from "@/context/AppContext";
import { PRESETS, autoDetectScale, connectScale, disconnectScale, getScaleConfig, sendPoll, setScaleConfig, useScale } from "@/lib/scale";
import { scaleCfg } from "@/lib/weigh";
import { BigButton, Chip, Field, inputCls } from "@/components/ui-kit";
import ScaleButton from "@/components/scale/ScaleButton";

const BAUDS = [1200, 2400, 4800, 9600, 19200];
const FORMATS = [{ k: "8N1", dataBits: 8, parity: "none" }, { k: "7E1", dataBits: 7, parity: "even" }];

function Calibration() {
  const { t } = useApp();
  const s = useScale();
  const [known, setKnown] = useState("1");
  const ref = Number(known.replace(",", ".")) || 0;
  const reading = s.kg - s.tare;
  const diff = Math.round((reading - ref) * 1000);
  const ok = Math.abs(reading - ref) <= Math.max(0.002, ref * 0.005);
  return (
    <div className="rounded-xl bg-secondary p-3 space-y-2" data-testid="scale-calibration">
      <div className="font-bold flex items-center gap-2"><Ruler className="w-4 h-4" />{t("scale_calibration")}</div>
      <div className="flex gap-2 items-center">
        <input dir="ltr" inputMode="decimal" value={known} onChange={(e) => setKnown(e.target.value)} className={`${inputCls} max-w-32`} data-testid="scale-cal-known" aria-label={t("scale_known_weight")} />
        <span className="text-sm">kg · {t("scale_known_weight")}</span>
      </div>
      {s.status === "connected" && ref > 0 && (
        <div className={`text-sm font-bold ${ok ? "text-[#1F9D55]" : "text-destructive"}`} dir="ltr" data-testid="scale-cal-result" data-ok={ok}>
          {reading.toFixed(3)} kg · {diff > 0 ? "+" : ""}{diff} g · {ok ? t("scale_cal_ok") : t("scale_cal_bad")}
        </div>
      )}
    </div>
  );
}

function DeviceScale() {
  const { t } = useApp();
  const s = useScale();
  const [cfg, setCfg] = useState(getScaleConfig);
  const [step, setStep] = useState("");
  const upd = (patch) => { const c = { ...cfg, ...patch }; setCfg(c); setScaleConfig(c); if (s.status === "connected") disconnectScale(); };
  const detect = async () => {
    setStep("…");
    const best = await autoDetectScale(setStep);
    setStep("");
    if (best) { setCfg(getScaleConfig()); toast.success(t("scale_detect_found", { v: `${best.baud} ${best.dataBits}${best.parity[0].toUpperCase()}1` })); connectScale(); } else toast.error(t("scale_detect_none"));
  };
  const fmt = FORMATS.find((f) => f.dataBits === Number(cfg.dataBits) && f.parity === cfg.parity)?.k;
  return (
    <div className="space-y-3" data-testid="scale-device-settings">
      <p className="text-sm text-muted-foreground">{t("scale_device_hint")}</p>
      <Field label={t("scale_preset")}>
        <div className="flex flex-wrap gap-2">{PRESETS.map((p) => (
          <Chip key={p.key} active={cfg.preset === p.key} onClick={() => upd({ preset: p.key, baud: p.baud, dataBits: p.dataBits, parity: p.parity, poll: p.poll })} data-testid={`scale-preset-${p.key}`}>{p.label}</Chip>
        ))}</div>
        <p className="text-xs text-muted-foreground mt-1">{t("scale_presets_hint")}</p>
      </Field>
      <Field label={t("scale_baud")}>
        <div className="flex flex-wrap gap-2">{BAUDS.map((b) => <Chip key={b} active={Number(cfg.baud) === b} onClick={() => upd({ baud: b })} data-testid={`scale-baud-${b}`}>{b}</Chip>)}</div>
      </Field>
      <Field label={t("scale_format")}>
        <div className="flex gap-2">{FORMATS.map((f) => <Chip key={f.k} active={fmt === f.k} onClick={() => upd({ dataBits: f.dataBits, parity: f.parity })} data-testid={`scale-format-${f.k}`}>{f.k}</Chip>)}</div>
      </Field>
      <Field label={t("scale_poll")}><input dir="ltr" value={cfg.poll} onChange={(e) => upd({ poll: e.target.value })} placeholder="W\r" className={inputCls} data-testid="scale-poll-input" /></Field>
      <label className="flex items-center justify-between gap-3 font-bold min-h-[52px]">
        <span>{t("scale_simulator")}<span className="block text-xs font-normal text-muted-foreground">{t("scale_simulator_hint")}</span></span>
        <Switch checked={!!cfg.sim} onCheckedChange={(v) => upd({ sim: v })} data-testid="scale-sim-switch" />
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <ScaleButton />
        <BigButton variant="soft" className="h-14" onClick={() => connectScale(true)} data-testid="scale-pick-port-btn"><Usb className="w-5 h-5" />{t("scale_pick_port")}</BigButton>
        <BigButton variant="soft" className="h-14" onClick={sendPoll} disabled={s.status !== "connected" || !cfg.poll} data-testid="scale-send-poll-btn"><Send className="w-5 h-5" />{t("scale_send_poll")}</BigButton>
        <BigButton variant="soft" className="h-14" onClick={detect} disabled={!!step || cfg.sim} data-testid="scale-autodetect-btn"><Radar className="w-5 h-5" />{step ? `${t("scale_detecting")} ${step}` : t("scale_autodetect")}</BigButton>
      </div>
      <div className="text-sm font-bold" data-testid="scale-settings-status">
        {s.status === "connected" ? `${(s.kg - s.tare).toFixed(3)} kg · ${s.stable ? t("scale_stable") : t("scale_settling")}` : s.status === "unsupported" ? t("scale_unsupported") : s.error || t("scale_not_connected")}
      </div>
      <div>
        <div className="text-xs font-bold text-muted-foreground mb-1">{t("scale_raw")}</div>
        <pre dir="ltr" className="h-40 overflow-y-auto rounded-xl bg-[#0D2D3D] text-[#5EE3A1] text-xs p-3 font-mono-receipt whitespace-pre-wrap" data-testid="scale-raw-monitor">
          {s.raw.length ? s.raw.join("\n") : "—"}
        </pre>
      </div>
      <Calibration />
    </div>
  );
}

function ShopScale() {
  const { t, view, dispatch } = useApp();
  const c = scaleCfg(view.shop);
  const [f, setF] = useState({ scale_mode: c.mode, scale_prefix: c.prefix, price_decimals: c.decimals, round_to: String(c.roundTo) });
  const save = () => {
    if (!/^2\d$/.test(f.scale_prefix)) return toast.error(t("scale_prefix_invalid"));
    if (dispatch("shop.update", { ...f, price_decimals: Number(f.price_decimals), round_to: Number(f.round_to) || 1 })) toast.success(t("saved"));
  };
  return (
    <div className="space-y-3 border-t border-border pt-3" data-testid="scale-shop-settings">
      <Field label={t("scale_label_carries")}>
        <div className="flex gap-2">
          <Chip active={f.scale_mode === "price"} onClick={() => setF({ ...f, scale_mode: "price" })} data-testid="scale-mode-price">{t("price")}</Chip>
          <Chip active={f.scale_mode === "weight"} onClick={() => setF({ ...f, scale_mode: "weight" })} data-testid="scale-mode-weight">{t("weight")}</Chip>
        </div>
      </Field>
      <div className="grid grid-cols-3 gap-3">
        <Field label={t("scale_prefix")}><input dir="ltr" inputMode="numeric" maxLength={2} value={f.scale_prefix} onChange={(e) => setF({ ...f, scale_prefix: e.target.value })} className={inputCls} data-testid="scale-prefix-input" /></Field>
        <Field label={t("price_decimals")}>
          <div className="flex gap-2">{[0, 2].map((d) => <Chip key={d} active={Number(f.price_decimals) === d} onClick={() => setF({ ...f, price_decimals: d })} data-testid={`scale-decimals-${d}`}>{d}</Chip>)}</div>
        </Field>
        <Field label={t("round_to")}><input dir="ltr" inputMode="decimal" value={f.round_to} onChange={(e) => setF({ ...f, round_to: e.target.value })} className={inputCls} data-testid="scale-round-input" /></Field>
      </div>
      <p className="text-xs text-muted-foreground" dir="ltr">{f.scale_prefix} · PLU(5) · {f.scale_mode === "weight" ? "grams(5)" : `price×10^${f.price_decimals}(5)`} · check</p>
      <BigButton onClick={save} className="w-full" data-testid="scale-settings-save-btn"><Save className="w-5 h-5" />{t("save")}</BigButton>
    </div>
  );
}

export default function ScaleSettings() {
  const { t, can } = useApp();
  return (
    <section className="rounded-2xl bg-white border border-border p-4 space-y-3" data-testid="scale-settings">
      <h2 className="font-heading text-xl font-bold flex items-center gap-2"><Scale className="w-5 h-5" />{t("scale_and_scanner")}</h2>
      <DeviceScale />
      {can("settings") && <ShopScale />}
    </section>
  );
}
